import { useEffect, useState } from 'react';
import { CheckCircle, X } from 'lucide-react';

interface SuccessToastProps {
  isVisible: boolean;
  message: string;
  onClose: () => void;
  autoClose?: boolean;
  duration?: number;
}

export function SuccessToast({ isVisible, message, onClose, autoClose = true, duration = 3000 }: SuccessToastProps) {
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isVisible) {
      setIsAnimating(true);
      
      if (autoClose) {
        const timer = setTimeout(() => {
          handleClose();
        }, duration);
        
        return () => clearTimeout(timer);
      }
    }
  }, [isVisible, autoClose, duration]);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      onClose();
    }, 300); // Wait for animation to complete
  };

  if (!isVisible) return null;

  return (
    <div className="fixed top-4 right-4 z-50 pointer-events-auto">
      <div 
        className={`
          flex items-center gap-3 bg-white border border-emerald-200/80 rounded-xl p-4 shadow-lg shadow-emerald-500/20
          transition-all duration-300 ease-out transform
          ${isAnimating ? 'translate-x-0 opacity-100 scale-100' : 'translate-x-full opacity-0 scale-95'}
        `}
      >
        {/* Icon */}
        <div className="w-8 h-8 rounded-full bg-emerald-100 border border-emerald-200/50 flex items-center justify-center shadow-sm">
          <CheckCircle className="w-4 h-4 text-emerald-600" />
        </div>
        
        {/* Message */}
        <div className="flex-1">
          <p className="text-sm font-semibold text-emerald-900">Success!</p>
          <p className="text-sm text-emerald-800">{message}</p>
        </div>
        
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="w-6 h-6 rounded-full hover:bg-emerald-100 flex items-center justify-center transition-colors"
        >
          <X className="w-3 h-3 text-emerald-600" />
        </button>
      </div>
    </div>
  );
} 